import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { PlayCircle } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { streamSse } from "@/lib/sse";
import { ApiError } from "@/lib/apiClient";
import { formatBytes, formatDateTime } from "@/lib/format";
import { useAuthServerTypes } from "@/lib/connectors";
import {
  ADMIN_TEST_MODES,
  INSTANCE_STATES,
  useAdminScenarios,
  type AdminProgress,
  type AdminResult,
  type AdminScenario,
  type AdminTestMode,
} from "@/lib/testAdmin";
import { Field, ProgressSteps, Row, TestHttpLogs, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";
import { Table } from "@/components/application/table/table";

export function TestAdminPage() {
  const { t } = useTranslation();
  const scenarios = useAdminScenarios();
  // Jen konektory tohoto buildu — dřív se nabízely všechny typy včetně cizích bank.
  const authServerTypes = useAuthServerTypes();

  const [scenario, setScenario] = useState("");
  const [mode, setMode] = useState<AdminTestMode>("TOKEN_SEARCH");
  // TOKEN_SEARCH
  const [usernameFilter, setUsernameFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [authServerTypeFilter, setAuthServerTypeFilter] = useState("");
  const [maxResults, setMaxResults] = useState("50");
  // BULK_DEACTIVATE
  const [tokenIds, setTokenIds] = useState("");
  const [deactivationReason, setDeactivationReason] = useState("");
  const [bypassAuthServer, setBypassAuthServer] = useState(true);
  const [bulkConfirmed, setBulkConfirmed] = useState(false);
  // LOG_EXPORT
  const [exportFormat, setExportFormat] = useState("JSON");
  const [daysToExport, setDaysToExport] = useState("7");
  // LOG_STATISTICS
  const [statisticsDays, setStatisticsDays] = useState("7");
  // LOG_CLEANUP
  const [retentionDays, setRetentionDays] = useState("30");
  const [dryRun, setDryRun] = useState(true);

  const [steps, setSteps] = useState<AdminProgress[]>([]);
  const [result, setResult] = useState<AdminResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const applyScenario = (name: string) => {
    setScenario(name);
    const s: AdminScenario | undefined = scenarios.data?.find((x) => x.name === name);
    if (!s) return;
    setMode(s.testMode);
    setBulkConfirmed(false);
    switch (s.testMode) {
      case "TOKEN_SEARCH":
        setUsernameFilter(s.usernameFilter ?? "");
        setStateFilter(s.stateFilter ?? "");
        setAuthServerTypeFilter(s.authServerTypeFilter ?? "");
        setMaxResults(String(s.maxResults ?? 50));
        break;
      case "BULK_DEACTIVATE":
        setTokenIds((s.tokenIds ?? []).join(", "));
        setDeactivationReason(s.deactivationReason ?? "");
        setBypassAuthServer(s.bypassAuthServer !== false);
        break;
      case "LOG_EXPORT":
        setExportFormat(s.exportFormat ?? "JSON");
        setDaysToExport(String(s.daysToExport ?? 7));
        break;
      case "LOG_STATISTICS":
        setStatisticsDays(String(s.daysToExport ?? 7));
        break;
      case "LOG_CLEANUP":
        setRetentionDays(String(s.retentionDays ?? 30));
        setDryRun(s.dryRun !== false);
        break;
    }
  };

  const start = async () => {
    setError(null);
    const body: Record<string, unknown> = { testMode: mode };
    switch (mode) {
      case "TOKEN_SEARCH":
        if (usernameFilter) body.usernameFilter = usernameFilter;
        if (stateFilter) body.stateFilter = stateFilter;
        if (authServerTypeFilter) body.authServerTypeFilter = authServerTypeFilter;
        body.maxResults = parseInt(maxResults, 10) || 50;
        break;
      case "BULK_DEACTIVATE": {
        const ids = tokenIds.split(",").map((x) => x.trim()).filter(Boolean);
        if (ids.length === 0) return setError(t("testing.admin.tokenIdsRequired"));
        if (!bulkConfirmed) return setError(t("testing.admin.bulkConfirmRequired"));
        body.tokenIds = ids;
        body.deactivationReason = deactivationReason;
        body.bypassAuthServer = bypassAuthServer;
        break;
      }
      case "HEALTH_CHECK":
        break;
      case "LOG_EXPORT":
        body.exportFormat = exportFormat;
        body.daysToExport = parseInt(daysToExport, 10) || 7;
        break;
      case "LOG_STATISTICS":
        body.daysToExport = parseInt(statisticsDays, 10) || 7;
        break;
      case "LOG_CLEANUP":
        body.retentionDays = parseInt(retentionDays, 10) || 30;
        body.dryRun = dryRun;
        break;
    }

    setSteps([]);
    setResult(null);
    setRunning(true);
    try {
      await streamSse("/admin/api/v1/testing/admin/start", body, (ev) => {
        if (ev.event !== "progress" || !ev.data) return;
        const progress = JSON.parse(ev.data) as AdminProgress;
        setSteps((s) => [...s, progress]);
        if (progress.status === "COMPLETED" && progress.data) setResult(progress.data);
        else if (progress.status === "FAILED") setError(progress.message);
      });
    } catch (e) {
      setError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const modeHelp: Record<AdminTestMode, string> = {
    TOKEN_SEARCH: t("testing.admin.help.search"),
    BULK_DEACTIVATE: t("testing.admin.help.bulk"),
    HEALTH_CHECK: t("testing.admin.help.health"),
    LOG_EXPORT: t("testing.admin.help.export"),
    LOG_STATISTICS: t("testing.admin.help.stats"),
    LOG_CLEANUP: t("testing.admin.help.cleanup"),
  };

  const destructive = mode === "BULK_DEACTIVATE";

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("testing.admin.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("testing.admin.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t("testing.activation.params")}</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("testing.admin.scenario")}>
            <AppSelect value={scenario} onChange={(e) => applyScenario(e.target.value)}>
              <option value="">{t("testing.admin.scenarioNone")}</option>
              {(scenarios.data ?? []).map((s) => (
                <option key={s.name} value={s.name}>
                  {s.category} — {s.name}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label={t("testing.admin.mode")} hint={modeHelp[mode]}>
            <AppSelect
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as AdminTestMode);
                setBulkConfirmed(false);
              }}
            >
              {ADMIN_TEST_MODES.map((m) => (
                <option key={m} value={m}>
                  {t(`testing.admin.mode.${m}`)}
                </option>
              ))}
            </AppSelect>
          </Field>
        </div>

        {/* Mode-specific fields */}
        {mode === "TOKEN_SEARCH" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("testing.admin.usernameFilter")}>
              <input className={inputCls} value={usernameFilter} onChange={(e) => setUsernameFilter(e.target.value)} />
            </Field>
            <Field label={t("testing.admin.stateFilter")}>
              <AppSelect value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                <option value="">{t("testing.admin.any")}</option>
                {INSTANCE_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </AppSelect>
            </Field>
            <Field label={t("testing.admin.authServerFilter")}>
              <AppSelect value={authServerTypeFilter} onChange={(e) => setAuthServerTypeFilter(e.target.value)}>
                <option value="">{t("testing.admin.any")}</option>
                {authServerTypes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </AppSelect>
            </Field>
            <Field label={t("testing.admin.maxResults")}>
              <input className={inputCls} type="number" min={1} max={1000} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} />
            </Field>
          </div>
        )}

        {mode === "BULK_DEACTIVATE" && (
          <div className="flex flex-col gap-4 rounded-lg bg-secondary p-4 ring-1 ring-error">
            <Field label={`${t("testing.admin.tokenIds")} *`} hint={t("testing.admin.tokenIdsHint")}>
              <textarea
                className={`${inputCls} h-20`}
                value={tokenIds}
                onChange={(e) => setTokenIds(e.target.value)}
              />
            </Field>
            <Field label={t("testing.admin.deactivationReason")}>
              <input className={inputCls} value={deactivationReason} onChange={(e) => setDeactivationReason(e.target.value)} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-primary">
              <input type="checkbox" checked={bypassAuthServer} onChange={(e) => setBypassAuthServer(e.target.checked)} />
              {t("testing.admin.bypassAuthServer")}
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-error-primary">
              <input type="checkbox" checked={bulkConfirmed} onChange={(e) => setBulkConfirmed(e.target.checked)} />
              {t("testing.admin.bulkConfirm")}
            </label>
          </div>
        )}

        {mode === "LOG_EXPORT" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("testing.admin.exportFormat")}>
              <AppSelect value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                <option value="JSON">JSON</option>
                <option value="CSV">CSV</option>
              </AppSelect>
            </Field>
            <Field label={t("testing.admin.daysToExport")}>
              <input className={inputCls} type="number" min={1} value={daysToExport} onChange={(e) => setDaysToExport(e.target.value)} />
            </Field>
          </div>
        )}

        {mode === "LOG_STATISTICS" && (
          <Field label={t("testing.admin.statisticsDays")}>
            <input className={`${inputCls} sm:max-w-xs`} type="number" min={1} value={statisticsDays} onChange={(e) => setStatisticsDays(e.target.value)} />
          </Field>
        )}

        {mode === "LOG_CLEANUP" && (
          <div className="flex flex-wrap items-end gap-6">
            <Field label={t("testing.admin.retentionDays")}>
              <input className={`${inputCls} w-40`} type="number" min={1} value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)} />
            </Field>
            <label className="flex items-center gap-2 pb-2 text-sm text-primary">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              {t("testing.admin.dryRun")}
            </label>
          </div>
        )}

        <div>
          <Button
            size="lg"
            color={destructive && !dryRun ? "primary-destructive" : "primary"}
            iconLeading={PlayCircle}
            isDisabled={running}
            onPress={() => void start()}
          >
            {running ? t("testing.running") : t("testing.admin.start")}
          </Button>
        </div>
      </div>

      <ProgressSteps steps={steps} />

      {result && <AdminResultView result={result} />}

      {error && !result && (
        <div className="rounded-xl bg-primary p-5 ring-1 ring-error">
          <h2 className="text-md font-semibold text-error-primary">✗ {t("testing.admin.failed")}</h2>
          <p className="mt-1 text-sm text-secondary">{error}</p>
        </div>
      )}

      {result && <TestHttpLogs logs={result.httpLogs ?? []} />}
    </div>
  );
}

function AdminResultView({ result }: { result: AdminResult }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-success-primary">✓ {t("testing.admin.success")}</h2>
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <Row label={t("testing.otp.mode")} value={t(`testing.admin.mode.${result.testMode}`)} />
        <Row label={t("testing.duration")} value={`${result.durationMs} ms`} />
        <Row label={t("testing.completedAt")} value={formatDateTime(result.completedAt)} />

        {result.testMode === "TOKEN_SEARCH" && (
          <Row label={t("testing.admin.totalMatches")} value={String(result.totalMatches ?? 0)} />
        )}
        {result.testMode === "BULK_DEACTIVATE" && (
          <>
            <Row label={t("testing.admin.deactivated")} value={String(result.deactivatedCount ?? 0)} />
            <Row label={t("testing.admin.failedCount")} value={String(result.failedCount ?? 0)} />
          </>
        )}
        {result.testMode === "HEALTH_CHECK" && (
          <>
            <Row label={t("testing.admin.healthStatus")} value={result.healthStatus ?? "—"} />
            <Row label={t("testing.admin.database")} value={result.databaseStatus ?? "—"} />
            <Row label={t("testing.admin.activeTokens")} value={String(result.activeTokens ?? 0)} />
          </>
        )}
        {result.testMode === "LOG_EXPORT" && (
          <>
            <Row label={t("testing.admin.exportFormat")} value={result.exportFormat ?? "—"} />
            <Row label={t("testing.admin.exportedLogs")} value={String(result.exportedLogCount ?? 0)} />
            <Row label={t("testing.admin.exportSize")} value={formatBytes(result.exportSizeBytes)} />
          </>
        )}
        {result.testMode === "LOG_STATISTICS" && (
          <>
            <Row label={t("testing.admin.totalLogs")} value={String(result.totalLogs ?? 0)} />
            <Row label={t("testing.admin.errors24h")} value={String(result.errorsLast24h ?? 0)} />
            <Row
              label={t("testing.admin.errorRate")}
              value={`${result.errorRateLast24h != null ? result.errorRateLast24h.toFixed(2) : "0"} %`}
            />
          </>
        )}
        {result.testMode === "LOG_CLEANUP" && (
          <>
            <Row
              label={t("testing.admin.deletedLogs")}
              value={`${result.deletedLogCount ?? 0}${result.dryRun ? ` (${t("testing.admin.dryRunLabel")})` : ""}`}
            />
            <Row label={t("testing.admin.retentionDays")} value={String(result.retentionDays ?? 0)} />
          </>
        )}
        <Row label={t("testing.httpCalls")} value={String(result.httpLogs?.length ?? 0)} />
      </dl>

      {/* Health server statuses */}
      {result.testMode === "HEALTH_CHECK" && result.authServerHealth && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(result.authServerHealth).map(([server, status]) => (
            <Badge key={server} type="pill-color" size="sm" color={/up|ok|healthy/i.test(status) ? "success" : "error"}>
              {server}: {status}
            </Badge>
          ))}
        </div>
      )}

      {/* Bulk deactivate errors */}
      {result.testMode === "BULK_DEACTIVATE" && result.deactivationErrors && result.deactivationErrors.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-error-primary">{t("testing.admin.errors")}</h4>
          <ul className="list-inside list-disc text-sm text-secondary">
            {result.deactivationErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Token search results */}
      {result.testMode === "TOKEN_SEARCH" && result.searchResults && result.searchResults.length > 0 && (
        <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
          <Table aria-label={t("testing.admin.mode.TOKEN_SEARCH")} selectionMode="none" size="sm">
            <Table.Header>
              <Table.Head isRowHeader id="tokenId" label={t("tokens.col.tokenId")} />
              <Table.Head id="username" label={t("tokens.col.username")} />
              <Table.Head id="state" label={t("tokens.col.state")} />
              <Table.Head id="authServerType" label={t("tokens.col.connector")} />
              <Table.Head id="createdAt" label={t("tokens.col.created")} />
            </Table.Header>
            <Table.Body items={result.searchResults}>
              {(token) => (
                <Table.Row id={token.tokenId}>
                  <Table.Cell>
                    <Link to="/tokens/$tokenId" params={{ tokenId: token.tokenId }} className="font-mono text-brand-secondary hover:underline">
                      {token.tokenId}
                    </Link>
                  </Table.Cell>
                  <Table.Cell className="text-secondary">{token.username ?? "—"}</Table.Cell>
                  <Table.Cell>
                    <Badge type="pill-color" size="sm" color={token.state === "ACTIVE" ? "success" : "gray"}>
                      {token.state}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-secondary">{token.authServerType}</Table.Cell>
                  <Table.Cell className="text-tertiary">{formatDateTime(token.createdAt)}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Statistics by auth server */}
      {result.testMode === "LOG_STATISTICS" && result.statsByAuthServer && Object.keys(result.statsByAuthServer).length > 0 && (
        <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
          <Table aria-label={t("testing.admin.mode.LOG_STATISTICS")} selectionMode="none" size="sm">
            <Table.Header>
              <Table.Head isRowHeader id="server" label={t("testing.admin.authServer")} />
              <Table.Head id="requests" label={t("testing.admin.requests")} />
              <Table.Head id="avg" label={t("testing.admin.avgResponse")} />
            </Table.Header>
            <Table.Body items={Object.entries(result.statsByAuthServer).map(([server, stats]) => ({ id: server, server, stats }))}>
              {(row) => (
                <Table.Row id={row.id}>
                  <Table.Cell className="text-secondary">{row.server}</Table.Cell>
                  <Table.Cell className="text-secondary">{row.stats.totalRequests ?? 0}</Table.Cell>
                  <Table.Cell className="text-secondary">
                    {row.stats.avgResponseTimeMs != null ? `${row.stats.avgResponseTimeMs.toFixed(0)} ms` : "—"}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <a
          href={`/admin/api/v1/testing/admin/export?format=json&minutes=60`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
        >
          {t("testing.exportJson")}
        </a>
        <a
          href={`/admin/api/v1/testing/admin/export?format=csv&minutes=60`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
        >
          {t("testing.exportCsv")}
        </a>
      </div>
    </div>
  );
}
